import { create } from "zustand";

export interface SearchResult {
  id: number;
  content: string;
  sourceType: "summary" | "message";
  sourceId: number;
  chunkIndex: number;
  metadata: {
    conversation_id?: string;
    conversation_type?: string;
    summary_date?: string;
    chat_id?: string;
    chat_name?: string;
    chat_type?: string;
    user_phone?: string;
    sender_phone?: string;
    sender_name?: string;
    timestamp?: string;
    platform?: string;
    is_dm?: boolean;
  };
  score: number;
}

interface SearchState {
  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Search state
  query: string;
  setQuery: (query: string) => void;

  results: SearchResult[];
  loading: boolean;
  error: string | null;
  tookMs: number | null;

  // Actions
  performSearch: (orgId: string) => Promise<void>;
  clearResults: () => void;

  // Selected message for detail modal
  selectedMessage: SearchResult | null;
  setSelectedMessage: (result: SearchResult | null) => void;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  // Modal state
  open: false,
  setOpen: (open) => set({ open }),

  // Search state
  query: "",
  setQuery: (query) => set({ query }),

  results: [],
  loading: false,
  error: null,
  tookMs: null,

  // Actions
  performSearch: async (orgId: string) => {
    const { query } = get();

    if (!query || query.trim().length < 3) {
      set({ results: [], error: null });
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await fetch(`/api/search?organizationId=${orgId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          query: query.trim(),
          limit: 10,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Search failed");
      }

      const data = await response.json();

      set({
        results: data.results || [],
        tookMs: data.took_ms,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Search error:", error);
      set({
        results: [],
        loading: false,
        error: error instanceof Error ? error.message : "Search failed",
        tookMs: null,
      });
    }
  },

  clearResults: () => set({ results: [], query: "", error: null, tookMs: null }),

  // Selected message
  selectedMessage: null,
  setSelectedMessage: (result) => set({ selectedMessage: result }),
}));
